<!-- src/lib/components/NavRail.svelte -->
<!--
  Narrow always-visible icon rail on the left of the shell. Linear-style.
  7 destinations, each one a "section" that swaps the contextual content
  sidebar to show that section's children. Icon-only with tooltip.

  The rail is 48px wide and never changes. The active section is
  highlighted with a purple left-border bar + filled-icon color.
-->
<script lang="ts">
  import { agentStore } from '../stores/agents.svelte.ts';
  import {
    pmStore,
    openWorkspaceSession,
    closeWorkspaceSession,
    saveActiveWorkspaceId,
    hydrateProjects,
    hydrateBoards,
  } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';

  export type NavSection = 'dev' | 'tasks' | 'agents' | 'studio' | 'drive' | 'chat' | 'settings';

  interface Props {
    activeSection: NavSection;
    onSelectSection: (section: NavSection) => void;
    /** Optional: click handler for the brand logo at the top of the rail */
    onBrandClick?: () => void;
  }
  let { activeSection, onSelectSection, onBrandClick }: Props = $props();

  // ── Round 7: workspace switcher dropdown ─────────────────────
  // Clicking the brand slot now toggles a dropdown listing every
  // open workspace session (status chip, unread badge, close × on
  // hover) + a "Switch to" row for any workspace not yet open.
  let wsOpen = $state(false);
  type HealthStatus = 'ok' | 'degraded' | 'down';
  interface HealthData {
    status: HealthStatus;
    queue_depth: number;
    running_agents: number;
    uptime_seconds: number;
  }

  const PINS_KEY = 'agentboard.pinnedProjects';
  let healthStatus = $state<HealthStatus>('ok');
  let healthData = $state<HealthData>({ status: 'ok', queue_depth: 0, running_agents: 0, uptime_seconds: 0 });
  let healthHover = $state(false);
  let allPins = $state<Record<string, string[]>>(loadPins());

  function loadPins(): Record<string, string[]> {
    try {
      const raw = localStorage.getItem(PINS_KEY);
      return raw ? JSON.parse(raw) as Record<string, string[]> : {};
    } catch {
      return {};
    }
  }

  function savePins(pins: Record<string, string[]>): void {
    try {
      localStorage.setItem(PINS_KEY, JSON.stringify(pins));
    } catch {
      // Ignore storage failures; pins remain session-local.
    }
  }

  async function pollHealth(): Promise<void> {
    try {
      const json = await api<{ status: string; queue_depth: number; running_agents: number; uptime_seconds: number }>('/health');
      const derived: HealthStatus = json.status !== 'ok'
        ? 'down'
        : json.queue_depth > 10
          ? 'degraded'
          : 'ok';
      healthStatus = derived;
      healthData = {
        status: derived,
        queue_depth: json.queue_depth,
        running_agents: json.running_agents,
        uptime_seconds: json.uptime_seconds,
      };
    } catch {
      healthStatus = 'down';
      healthData = { status: 'down', queue_depth: 0, running_agents: 0, uptime_seconds: 0 };
    }
  }

  $effect(() => {
    void pollHealth();
    const interval = setInterval(() => {
      void pollHealth();
    }, 30_000);
    return () => clearInterval(interval);
  });

  const openSessions = $derived(
    pmStore.openWorkspaceIds
      .map((id) => pmStore.workspaces.find((w) => w.id === id))
      .filter((w): w is NonNullable<typeof w> => !!w)
  );
  const closedWorkspaces = $derived(
    pmStore.workspaces.filter((w) => !pmStore.openWorkspaceIds.includes(w.id))
  );

  async function switchToWorkspace(wsId: string) {
    if (pmStore.activeWorkspaceId === wsId) { wsOpen = false; return; }
    openWorkspaceSession(wsId);
    pmStore.activeWorkspaceId = wsId;
    saveActiveWorkspaceId(wsId);
    pmStore.activeProjectId   = null;
    pmStore.activeBoardId     = null;
    wsOpen = false;
    await hydrateProjects(wsId);
  }

  async function jumpToProject(wsId: string, projectId: string): Promise<void> {
    if (pmStore.activeWorkspaceId !== wsId) {
      openWorkspaceSession(wsId);
      pmStore.activeWorkspaceId = wsId;
      await hydrateProjects(wsId);
    }
    pmStore.activeProjectId = projectId;
    pmStore.activeBoardId = null;
    wsOpen = false;
    await hydrateBoards(projectId);
  }

  function pinnedForWorkspace(wsId: string): string[] {
    return allPins[wsId] ?? [];
  }

  function isPinned(wsId: string, projectId: string): boolean {
    return pinnedForWorkspace(wsId).includes(projectId);
  }

  function togglePin(e: MouseEvent, wsId: string, projectId: string): void {
    e.stopPropagation();
    const pins = loadPins();
    const existing = pins[wsId] ?? [];
    pins[wsId] = existing.includes(projectId)
      ? existing.filter((id) => id !== projectId)
      : [...existing, projectId];
    savePins(pins);
    allPins = pins;
  }

  function formatUptime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }

  async function closeSession(e: MouseEvent, wsId: string) {
    e.stopPropagation();
    const nextActive = closeWorkspaceSession(wsId);
    if (nextActive && nextActive !== wsId) {
      await hydrateProjects(nextActive);
    }
  }

  function handleBrandClick() {
    wsOpen = !wsOpen;
    if (onBrandClick) onBrandClick();
  }

  // Outside-click detection. We use POINTERDOWN (not click) because
  // Svelte 5 flushes reactive state updates synchronously inside click
  // handlers — which means the dropdown can unmount mid-bubble. At
  // pointerdown time the DOM is still intact, so target.closest() is
  // authoritative.
  function onWindowPointerDown(e: PointerEvent) {
    if (!wsOpen) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('.ws-dropdown')) return;
    if (target?.closest('.nav-brand')) return;
    wsOpen = false;
  }

  // Each destination gets an icon (inline SVG to keep the rail dep-free)
  // and a label for the tooltip + aria-label. Badges are computed from
  // live state (running agent count, etc).
  interface NavDest {
    id:     NavSection;
    label:  string;
    short:  string;
    /** Lucide-style stroke SVG path d-string inside a 24×24 viewbox */
    path:   string;
  }

  // 7 top-level destinations matching the Round 0 refinement plan:
  // Dev / Tasks / Agents / Studio / Drive / Chat / Settings.
  // Order matters — dev is first because that's where most work starts
  // in this tool; settings is always last.
  const destinations: NavDest[] = [
    {
      id: 'dev',
      label: 'Development',
      short: 'Dev',
      // Code brackets / terminal — </> style
      path: 'M16 18l6-6-6-6M8 6l-6 6 6 6',
    },
    {
      id: 'tasks',
      label: 'Tasks',
      short: 'Tasks',
      // Columns / kanban board icon (was the old 'project' icon — tasks
      // is now kanban-first so the columns glyph fits).
      path: 'M3 3h18v18H3zM9 3v18M15 3v18',
    },
    {
      id: 'agents',
      label: 'Agents',
      short: 'Agents',
      // Bot / cpu icon
      path: 'M4 6h16v12H4zM8 2v4M16 2v4M4 10h16M9 14h.01M15 14h.01',
    },
    {
      id: 'studio',
      label: 'Studio',
      short: 'Studio',
      // Pen / design — diamond with stroke
      path: 'M12 19l7-7 3 3-7 7-3-3zM18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5zM2 2l7.586 7.586M11 11a2 2 0 11-2-2',
    },
    {
      id: 'drive',
      label: 'Drive',
      short: 'Drive',
      // Cloud / folder — cloud glyph
      path: 'M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z',
    },
    {
      id: 'chat',
      label: 'Chat',
      short: 'Chat',
      // Message bubble
      path: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
    },
    {
      id: 'settings',
      label: 'Settings',
      short: 'Settings',
      // Gear icon
      path: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
    },
  ];

  const runningAgents = $derived(agentStore.running.length);
  const chatUnread = $derived(
    pmStore.activeWorkspaceId
      ? (pmStore.sessionMeta[pmStore.activeWorkspaceId]?.unreadCount ?? 0)
      : 0
  );
</script>

<svelte:window onpointerdown={onWindowPointerDown} />

<aside class="nav-rail" role="navigation" aria-label="Primary">
  <!-- Brand logo doubles as a workspace-switcher toggle (Round 7).
       The inner dot color reflects the active session's status. -->
  <button
    class="nav-brand"
    class:active={wsOpen}
    class:health-ok={healthStatus === 'ok'}
    class:health-degraded={healthStatus === 'degraded'}
    class:health-down={healthStatus === 'down'}
    onclick={handleBrandClick}
    onmouseenter={() => { healthHover = true; }}
    onmouseleave={() => { healthHover = false; }}
    title="Workspace switcher"
    aria-label="Workspace switcher"
    aria-haspopup="menu"
    aria-expanded={wsOpen}
  >
    <span
      class="brand-dot"
      class:pulsing={agentStore.running.length > 0}
      aria-hidden="true"
    ></span>
  </button>

  {#if healthHover && !wsOpen}
    <div class="health-popover" role="tooltip" aria-label="System health">
      <div class="health-header">
        <span
          class="health-status-dot"
          class:ok={healthStatus === 'ok'}
          class:degraded={healthStatus === 'degraded'}
          class:down={healthStatus === 'down'}
          aria-hidden="true"
        ></span>
        <span class="health-status-label">
          {healthStatus === 'ok' ? 'System healthy' : healthStatus === 'degraded' ? 'Queue deep' : 'Backend down'}
        </span>
      </div>
      <div class="health-rows">
        <div class="health-row">
          <span class="health-key">Queue</span>
          <span class="health-val">{healthData.queue_depth}</span>
        </div>
        <div class="health-row">
          <span class="health-key">Running</span>
          <span class="health-val">{healthData.running_agents}</span>
        </div>
        <div class="health-row">
          <span class="health-key">Uptime</span>
          <span class="health-val">{formatUptime(healthData.uptime_seconds)}</span>
        </div>
      </div>
    </div>
  {/if}

  {#if wsOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="ws-dropdown" role="menu">
      <div class="ws-section-label">Open sessions</div>
      {#if openSessions.length === 0}
        <div class="ws-empty">No open sessions.</div>
      {/if}
      {#each openSessions as ws (ws.id)}
        {@const sessionRunning = pmStore.sessionMeta[ws.id]?.runningAgents ?? 0}
        {@const isActiveWorkspace = pmStore.activeWorkspaceId === ws.id}
        {@const sessionPins = pinnedForWorkspace(ws.id)}
        {@const pinnedProjects = (isActiveWorkspace ? sessionPins : [])
          .map((id) => pmStore.projects.find((p) => p.id === id))
          .filter((project): project is NonNullable<typeof project> => !!project)}
        <div
          class="ws-row"
          class:active={isActiveWorkspace}
          onclick={() => switchToWorkspace(ws.id)}
        >
          <span class="ws-status-dot" class:running={(pmStore.sessionMeta[ws.id]?.runningAgents ?? 0) > 0}></span>
          <div class="ws-info">
            <div class="ws-name">{ws.name}</div>
            <div class="ws-meta">
              <span class="ws-slug">{ws.slug}</span>
              {#if sessionRunning > 0}
                <span class="ws-running-count">{sessionRunning} running</span>
              {/if}
            </div>
          </div>
          {#if (pmStore.sessionMeta[ws.id]?.unreadCount ?? 0) > 0}
            <span class="ws-unread-badge">{pmStore.sessionMeta[ws.id]?.unreadCount}</span>
          {/if}
          {#if openSessions.length > 1}
            <button
              class="ws-close"
              aria-label="Close session"
              title="Close session"
              onclick={(e) => closeSession(e, ws.id)}
            >×</button>
          {/if}
        </div>
        {#if pinnedProjects.length > 0}
          <div class="ws-pins">
            {#each pinnedProjects as proj (proj.id)}
              <div class="ws-pin-row">
                <button
                  class="ws-pin-jump"
                  onclick={() => void jumpToProject(ws.id, proj.id)}
                  title="Jump to {proj.name}"
                >
                  <span class="ws-pin-icon" aria-hidden="true">★</span>
                  <span class="ws-pin-name">{proj.name}</span>
                </button>
                <button
                  class="ws-unpin-btn"
                  aria-label="Unpin {proj.name}"
                  onclick={(e) => togglePin(e, ws.id, proj.id)}
                >×</button>
              </div>
            {/each}
          </div>
        {/if}
        {#if isActiveWorkspace && pmStore.projects.length > 0}
          <div class="ws-project-list">
            {#each pmStore.projects.slice(0, 5) as proj (proj.id)}
              <div class="ws-project-row">
                <button
                  class="ws-project-jump"
                  onclick={() => void jumpToProject(ws.id, proj.id)}
                  title={proj.name}
                >
                  <span class="ws-project-name">{proj.name}</span>
                </button>
                <button
                  class="ws-star-btn"
                  class:pinned={isPinned(ws.id, proj.id)}
                  aria-label="{isPinned(ws.id, proj.id) ? 'Unpin' : 'Pin'} {proj.name}"
                  title="{isPinned(ws.id, proj.id) ? 'Unpin' : 'Pin'}"
                  onclick={(e) => togglePin(e, ws.id, proj.id)}
                >★</button>
              </div>
            {/each}
            {#if pmStore.projects.length > 5}
              <span class="ws-more-projects">+{pmStore.projects.length - 5} more</span>
            {/if}
          </div>
        {/if}
      {/each}

      {#if closedWorkspaces.length > 0}
        <div class="ws-divider"></div>
        <div class="ws-section-label">Other workspaces</div>
        {#each closedWorkspaces as ws (ws.id)}
          <div
            class="ws-row closed"
            onclick={() => switchToWorkspace(ws.id)}
          >
            <span class="ws-status-dot"></span>
            <div class="ws-info">
              <div class="ws-name">{ws.name}</div>
              <div class="ws-slug">{ws.slug}</div>
            </div>
          </div>
        {/each}
      {/if}
    </div>
  {/if}

  <div class="nav-destinations">
    {#each destinations as d (d.id)}
      <button
        class="nav-btn"
        class:active={activeSection === d.id}
        onclick={() => onSelectSection(d.id)}
        title={d.label}
        aria-label={d.label}
        aria-current={activeSection === d.id ? 'page' : undefined}
      >
        <svg
          viewBox="0 0 24 24"
          width="22"
          height="22"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="nav-icon"
        >
          <path d={d.path} />
        </svg>
        <span class="nav-label">{d.short}</span>
        {#if d.id === 'agents' && runningAgents > 0}
          <span class="nav-badge">{runningAgents}</span>
        {:else if d.id === 'chat' && chatUnread > 0}
          <span class="nav-badge">{chatUnread > 9 ? '9+' : chatUnread}</span>
        {/if}
      </button>
    {/each}
  </div>
</aside>

<style>
  .nav-rail {
    width: 60px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.7rem 0 0.85rem 0;
    /* Darker than the content sidebar so the rail recedes slightly */
    background: oklch(0.07 0.014 275);
    border-right: 1px solid oklch(0.35 0.06 275 / 0.35);
    position: relative;
    z-index: 5;
  }
  .nav-rail::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0; right: 0;
    width: 1px;
    background: linear-gradient(180deg,
      transparent 0%,
      oklch(0.72 0.28 310 / 0.22) 50%,
      transparent 100%);
    opacity: 0.6;
    pointer-events: none;
  }

  /* ── Brand ────────────────────────────────────────────────────── */
  .nav-brand {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: oklch(0.11 0.02 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-bottom: 0.85rem;
    transition: background-color 100ms ease, border-color 100ms ease;
  }
  .nav-brand:hover,
  .nav-brand.active {
    background: oklch(0.72 0.28 310 / 0.10);
    border-color: oklch(0.72 0.28 310 / 0.35);
  }

  /* ── Round 7: Workspace switcher dropdown ─────────────────────── */
  .ws-dropdown {
    position: absolute;
    top: 0.7rem;
    left: 68px;
    z-index: 100;
    min-width: 260px;
    max-width: 320px;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.45);
    border-radius: 6px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
    padding: 0.45rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .ws-section-label {
    font-size: 0.58rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.55 0.04 310 / 0.55);
    padding: 0.35rem 0.55rem 0.1rem;
  }
  .ws-empty {
    font-size: 0.72rem;
    color: oklch(0.55 0.04 310 / 0.55);
    padding: 0.25rem 0.55rem 0.4rem;
  }
  .ws-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.4rem 0.55rem;
    border-radius: 4px;
    cursor: pointer;
    transition: background 100ms ease;
  }
  .ws-row:hover {
    background: oklch(0.17 0.025 275);
  }
  .ws-row.active {
    background: oklch(0.22 0.08 310 / 0.35);
  }
  .ws-row.closed {
    opacity: 0.68;
  }
  .ws-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: oklch(0.50 0.04 310 / 0.45);
    flex-shrink: 0;
  }
  .ws-status-dot.running {
    background: oklch(0.78 0.16 150);
    box-shadow: 0 0 6px oklch(0.78 0.16 150 / 0.55);
    animation: ws-pulse 1.5s ease-in-out infinite;
  }
  .ws-row.active .ws-status-dot {
    background: oklch(0.80 0.26 310);
    box-shadow: 0 0 6px oklch(0.72 0.28 310 / 0.55);
  }
  @keyframes ws-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.45; }
  }
  .ws-info {
    flex: 1;
    min-width: 0;
  }
  .ws-name {
    font-size: 0.78rem;
    font-weight: 500;
    color: oklch(0.94 0.08 310);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ws-slug {
    font-size: 0.64rem;
    color: oklch(0.55 0.04 310 / 0.55);
    font-family: 'JetBrains Mono', monospace;
  }
  .ws-meta {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.08rem;
    min-width: 0;
  }
  .ws-running-count {
    font-size: 0.58rem;
    font-weight: 600;
    color: oklch(0.72 0.18 145 / 0.85);
    font-family: 'JetBrains Mono', monospace;
    flex-shrink: 0;
  }
  .ws-unread-badge {
    font-size: 0.62rem;
    font-weight: 600;
    min-width: 18px;
    height: 18px;
    padding: 0 0.35rem;
    border-radius: 9px;
    background: oklch(0.62 0.26 25);
    color: oklch(0.98 0.04 25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ws-close {
    width: 20px;
    height: 20px;
    background: transparent;
    border: none;
    color: oklch(0.50 0.04 310 / 0.55);
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    border-radius: 3px;
    opacity: 0;
    transition: opacity 100ms ease, background 100ms ease, color 100ms ease;
    flex-shrink: 0;
  }
  .ws-row:hover .ws-close {
    opacity: 1;
  }
  .ws-close:hover {
    background: oklch(0.62 0.26 25 / 0.25);
    color: oklch(0.92 0.14 25);
  }
  .ws-divider {
    height: 1px;
    background: oklch(0.30 0.05 275 / 0.30);
    margin: 0.35rem 0.25rem;
  }
  .ws-pins {
    padding: 0.1rem 0.35rem 0.25rem 1.55rem;
    display: flex;
    flex-direction: column;
    gap: 0.06rem;
  }
  .ws-pin-row,
  .ws-project-row {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    width: 100%;
    border-radius: 3px;
    transition: background 80ms ease;
  }
  .ws-pin-row:hover,
  .ws-project-row:hover {
    background: oklch(0.17 0.025 275);
  }
  .ws-pin-jump,
  .ws-project-jump {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    background: transparent;
    border: none;
    padding: 0.18rem 0.35rem;
    color: inherit;
    cursor: pointer;
    text-align: left;
  }
  .ws-pin-icon {
    font-size: 0.6rem;
    color: oklch(0.80 0.22 75);
    flex-shrink: 0;
  }
  .ws-pin-name {
    flex: 1;
    font-size: 0.68rem;
    color: oklch(0.82 0.06 310);
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ws-unpin-btn {
    background: transparent;
    border: none;
    color: oklch(0.45 0.03 310 / 0.55);
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0;
    width: 14px;
    height: 14px;
    line-height: 1;
    opacity: 0;
    border-radius: 2px;
    transition: opacity 80ms ease, color 80ms ease;
    flex-shrink: 0;
  }
  .ws-pin-row:hover .ws-unpin-btn {
    opacity: 1;
  }
  .ws-unpin-btn:hover {
    color: oklch(0.72 0.20 25);
  }
  .ws-project-list {
    padding: 0.1rem 0.35rem 0.3rem 1.55rem;
    display: flex;
    flex-direction: column;
    gap: 0.04rem;
    border-top: 1px solid oklch(0.22 0.04 275 / 0.25);
    margin-top: 0.1rem;
  }
  .ws-project-jump {
    justify-content: space-between;
  }
  .ws-project-name {
    font-size: 0.68rem;
    color: oklch(0.75 0.05 310 / 0.85);
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }
  .ws-star-btn {
    background: transparent;
    border: none;
    font-size: 0.65rem;
    color: oklch(0.40 0.03 310 / 0.45);
    cursor: pointer;
    padding: 0;
    width: 16px;
    height: 16px;
    line-height: 1;
    flex-shrink: 0;
    border-radius: 2px;
    opacity: 0;
    transition: opacity 80ms ease, color 80ms ease;
  }
  .ws-project-row:hover .ws-star-btn {
    opacity: 1;
  }
  .ws-star-btn.pinned {
    color: oklch(0.80 0.22 75);
    opacity: 1;
  }
  .ws-star-btn:hover {
    color: oklch(0.80 0.22 75);
  }
  .ws-more-projects {
    font-size: 0.60rem;
    color: oklch(0.45 0.03 310 / 0.50);
    padding: 0.1rem 0.35rem;
  }
  .nav-brand.health-ok .brand-dot {
    background: radial-gradient(circle at 35% 35%,
      oklch(0.88 0.16 145) 0%,
      oklch(0.72 0.18 145) 40%,
      oklch(0.58 0.18 145) 100%);
    box-shadow:
      0 0 3px oklch(0.80 0.18 145 / 0.9),
      0 0 9px oklch(0.72 0.18 145 / 0.6),
      0 0 18px oklch(0.62 0.18 145 / 0.3);
  }
  .nav-brand.health-degraded .brand-dot {
    background: radial-gradient(circle at 35% 35%,
      oklch(0.92 0.18 75) 0%,
      oklch(0.82 0.18 75) 40%,
      oklch(0.68 0.18 75) 100%);
    box-shadow:
      0 0 3px oklch(0.88 0.18 75 / 0.9),
      0 0 9px oklch(0.82 0.18 75 / 0.6),
      0 0 18px oklch(0.72 0.18 75 / 0.3);
  }
  .nav-brand.health-down .brand-dot {
    background: radial-gradient(circle at 35% 35%,
      oklch(0.82 0.22 25) 0%,
      oklch(0.62 0.22 25) 40%,
      oklch(0.48 0.22 25) 100%);
    box-shadow:
      0 0 3px oklch(0.72 0.22 25 / 0.9),
      0 0 9px oklch(0.62 0.22 25 / 0.6),
      0 0 18px oklch(0.50 0.22 25 / 0.3);
    animation: none;
  }
  .brand-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%,
      oklch(0.88 0.16 145) 0%,
      oklch(0.72 0.18 145) 40%,
      oklch(0.58 0.18 145) 100%);
    box-shadow:
      0 0 3px oklch(0.80 0.18 145 / 0.9),
      0 0 9px oklch(0.72 0.18 145 / 0.6),
      0 0 18px oklch(0.62 0.18 145 / 0.3);
    will-change: transform, opacity;
    animation: brand-breathe var(--pulse-rate, 10s) ease-in-out infinite;
  }
  @keyframes brand-breathe {
    0%, 100% { transform: scale(1);    opacity: 1;    }
    50%       { transform: scale(0.78); opacity: 0.55; }
  }
  @keyframes health-pulse {
    0%, 100% { box-shadow: 0 0 3px oklch(0.80 0.18 145 / 0.9), 0 0 9px oklch(0.72 0.18 145 / 0.6), 0 0 18px oklch(0.62 0.18 145 / 0.3); opacity: 1; }
    50%       { box-shadow: 0 0 3px oklch(0.80 0.18 145 / 0.9), 0 0 9px oklch(0.72 0.18 145 / 0.6), 0 0 18px oklch(0.62 0.18 145 / 0.3), 0 0 0 4px oklch(0.72 0.28 310 / 0); opacity: 0.85; }
  }
  .brand-dot.pulsing {
    animation: health-pulse 1.8s ease-in-out infinite;
    color: oklch(0.72 0.28 310 / 0.4);
  }
  @media (prefers-reduced-motion: reduce) {
    .brand-dot { animation: none; }
    .brand-dot.pulsing { animation: none; }
  }

  /* ── Destinations ─────────────────────────────────────────────── */
  .health-popover {
    position: absolute;
    top: 0.7rem;
    left: 68px;
    z-index: 101;
    width: 180px;
    background: oklch(0.095 0.018 275);
    border: 1px solid oklch(0.35 0.06 275 / 0.45);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
    padding: 0.55rem;
  }
  .health-header {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px solid oklch(0.25 0.04 275 / 0.35);
    margin-bottom: 0.4rem;
  }
  .health-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .health-status-dot.ok {
    background: oklch(0.72 0.18 145);
    box-shadow: 0 0 5px oklch(0.72 0.18 145 / 0.6);
  }
  .health-status-dot.degraded {
    background: oklch(0.82 0.18 75);
    box-shadow: 0 0 5px oklch(0.82 0.18 75 / 0.6);
  }
  .health-status-dot.down {
    background: oklch(0.62 0.22 25);
    box-shadow: 0 0 5px oklch(0.62 0.22 25 / 0.6);
  }
  .health-status-label {
    font-size: 0.72rem;
    font-weight: 600;
    color: oklch(0.88 0.06 310);
  }
  .health-rows {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .health-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .health-key {
    font-size: 0.64rem;
    color: oklch(0.55 0.04 310 / 0.60);
  }
  .health-val {
    font-size: 0.68rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.85 0.06 310);
  }
  .nav-destinations {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    flex: 1;
  }
  .nav-btn {
    width: 46px;
    height: auto;
    min-height: 46px;
    border-radius: 10px;
    background: transparent;
    border: none;
    color: oklch(0.60 0.04 310 / 0.7);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    position: relative;
    padding: 0.35rem 0;
    transition: background-color 100ms ease, color 100ms ease;
  }
  .nav-btn:hover {
    background: oklch(0.72 0.28 310 / 0.08);
    color: oklch(0.88 0.08 310);
  }
  .nav-btn.active {
    background: oklch(0.72 0.28 310 / 0.14);
    color: oklch(0.94 0.14 310);
  }
  .nav-btn.active::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 22px;
    background: oklch(0.80 0.26 310);
    border-radius: 0 2px 2px 0;
    box-shadow: 0 0 8px oklch(0.80 0.26 310 / 0.6);
  }
  .nav-icon {
    flex-shrink: 0;
  }
  .nav-label {
    font-size: 0.48rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    line-height: 1;
    color: inherit;
    opacity: 0.7;
    pointer-events: none;
    user-select: none;
  }
  .nav-btn.active .nav-label {
    opacity: 1;
  }

  .nav-badge {
    position: absolute;
    top: 3px;
    right: 4px;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    background: oklch(0.78 0.26 310);
    color: oklch(0.08 0.02 275);
    border-radius: 7px;
    font-size: 0.6rem;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
    line-height: 14px;
    text-align: center;
    box-shadow: 0 0 6px oklch(0.72 0.28 310 / 0.5);
  }
</style>
